export const calculateTotalPosts = (data) => {
  if (!Array.isArray(data)) {
    console.error("Invalid data passed to calculateTotalPosts:", data);
    return 0;
  }

  return data.reduce((sum, item) => sum + (Array.isArray(item.posts) ? item.posts.length : 0), 0);
};

export const calculateTotalReactions = (data) => {
  if (!Array.isArray(data)) {
    console.error("Invalid data passed to calculateTotalReactions:", data);
    return 0;
  }

  return data.reduce(
    (sum, item) =>
      sum +
      (Array.isArray(item.posts)
        ? item.posts.reduce(
          (postSum, post) =>
            postSum +
            (post.postMetrics?.likes || post.postMetrics?.like || 0) +
            (post.postMetrics?.love || 0) +
            (post.postMetrics?.wow || 0) +
            (post.postMetrics?.haha || 0) +
            (post.postMetrics?.sad || 0) +
            (post.postMetrics?.angry || 0) +
            (post.postMetrics?.comments || 0) +
            (post.postMetrics?.shares || 0),
          0
        )
        : 0),
    0
  );
};

export const calculateTotalViews = (data) => {
  // Sum up view counts from all posts in the dataset.
  // Checks for postMetrics.views (General Post/Instagram/Facebook) and videoMetrics.viewCount (YouTube).
  return data.reduce(
    (sum, item) =>
      sum +
      (item.posts?.reduce(
        (postSum, post) =>
          postSum +
          (post.postMetrics?.post_video_views ||
            post.postMetrics?.video_views ||
            post.postMetrics?.view_count ||
            post.postMetrics?.viewCount ||
            post.postMetrics?.views || 0),
        0
      ) || 0),
    0
  );
};

/**
 * Calculates the total page impressions by summing 'pageMetrics.page_impressions' across all daily data points.
 * @param {Array<Object>} data - Array of daily data objects.
 * @returns {number} The total number of page impressions.
 */
export const calculateTotalImpressions = (data) => {
  if (!Array.isArray(data)) {
    console.error("Invalid data passed to calculateTotalImpressions:", data);
    return 0;
  }

  return data.reduce(
    (sum, item) => sum + (item.pageMetrics?.page_impressions || item.pageMetrics?.page_media_view || 0),
    0
  );
};

/**
 * Calculates the total page reach by summing 'pageMetrics.page_impressions_unique' across all daily data points.
 * Reach is typically defined as unique users who saw the content (unique impressions).
 * @param {Array<Object>} data - Array of daily data objects.
 * @returns {number} The total number of unique page impressions (Total Reach).
 */
export const calculateTotalReach = (data) => {
  if (!Array.isArray(data)) {
    console.error("Invalid data passed to calculateTotalReach:", data);
    return 0;
  }

  return data.reduce(
    (sum, item) => sum + (item.pageMetrics?.page_impressions_unique || 0),
    0
  );
};

/**
 * Calculates the total post impressions by summing 'postMetrics.post_impressions' 
 * across all posts in the entire dataset.
 * @param {Array<Object>} data - Array of daily data objects, each potentially containing an array of posts.
 * @returns {number} The total number of post impressions.
 */
export const calculateTotalPostImpressions = (data) => {
  if (!Array.isArray(data)) {
    console.error("Invalid data passed to calculateTotalPostImpressions:", data);
    return 0;
  }

  return data.reduce(
    (dailySum, item) =>
      dailySum +
      (Array.isArray(item.posts)
        ? item.posts.reduce(
          (postSum, post) => postSum + (post.postMetrics?.post_media_view || post.postMetrics?.post_views || 0),
          0
        )
        : 0),
    0
  );
};

// ---

/**
 * Calculates the total unique post reach by summing 'postMetrics.post_impressions_unique' 
 * across all posts in the entire dataset.
 * @param {Array<Object>} data - Array of daily data objects, each potentially containing an array of posts.
 * @returns {number} The total unique post reach.
 */
export const calculateTotalPostReach = (data) => {
  if (!Array.isArray(data)) {
    console.error("Invalid data passed to calculateTotalPostReach:", data);
    return 0;
  }

  return data.reduce(
    (dailySum, item) =>
      dailySum +
      (Array.isArray(item.posts)
        ? item.posts.reduce(
          (postSum, post) => postSum + (post.postMetrics?.post_impressions_unique || 0),
          0
        )
        : 0),
    0
  );
};

/**
 * Calculates the total post clicks by summing 'postMetrics.post_clicks' 
 * across all posts in the entire dataset.
 * @param {Array<Object>} data - Array of daily data objects, each potentially containing an array of posts.
 * @returns {number} The total number of post clicks.
 */
export const calculateTotalPostClicks = (data) => {
  if (!Array.isArray(data)) {
    console.error("Invalid data passed to calculateTotalPostClicks:", data);
    return 0;
  }

  return data.reduce(
    (dailySum, item) =>
      dailySum +
      (Array.isArray(item.posts)
        ? item.posts.reduce(
          (postSum, post) => postSum + (post.postMetrics?.post_clicks || 0),
          0
        )
        : 0),
    0
  );
};